import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { inject, observer } from 'mobx-react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, FormControlLabel, Grid, TextField, Typography, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/styles';

import { CustomButton } from '../components/button';
import { PhoneVerify } from '../components/verify';
import { AuthTabs, BizCertField } from '../components/auth';
import CustomCheckbox from '../components/button/CustomCheckbox';
import GuideMessage from '../components/message/GuideMessage';
import { EmailRegExp, PwdRegExp } from '../lib/util';

// 회원가입 — 개인회원은 바로 가입, 기업회원은 상호·사업자등록번호(·사업자등록증 첨부)를 받아 [신청하기](직원 승인 뒤 로그인). 의뢰자 정보(부서·주소)는 마이페이지 [의뢰자 정보 설정] 또는 마지막 접수로 채운다
const useStyles = makeStyles((theme) => ({
  signUpWrap: {
    paddingTop: 100,
    paddingBottom: 150,
    [theme.breakpoints.down('sm')]: { paddingTop: 30, paddingBottom: 100 },
  },
  card: {
    width: 420,
    maxWidth: 'calc(100vw - 40px)',
  },
  tabs: { marginTop: 40, marginBottom: 24 },
  formUserInfoWrap: {
    position: 'relative',
    paddingBottom: 20,
  },
  termsLink: {
    paddingTop: 40,
    paddingBottom: 60,
  },
  termsText: {
    fontSize: 18,
    color: '#555558',
    '& a': {
      marginLeft: 6,
      color: '#f77c40',
      fontSize: '16px',
      cursor: 'pointer',
    },
  },
}));

const emptyForm = () => ({ email: '', password1: '', password: '', name: '', companyName: '', bizNo: '' });

// 사업자등록번호 — 숫자 10자리를 000-00-00000 로 보여 준다
const formatBizNo = (v) => {
  const d = String(v || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

function SignUpPage({ loginStore, history, location }) {
  const classes = useStyles();
  const { emailJoin, phoneInitial, setPhoneVerified } = loginStore;
  const [userType, setUserType] = useState((location.state && location.state.userType) === 'Company' ? 'Company' : 'Private');
  const [form, setForm] = useState(emptyForm);
  const [bizCert, setBizCert] = useState(null);   // 기업회원 — 사업자등록증 파일(이미지·PDF 1장), multipart 로 보낸다
  const [trems, setTrems] = useState([false, false, false]);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => () => { phoneInitial(); }, [phoneInitial]);

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === 'bizNo' ? formatBizNo(value) : value }));
  };

  const handleTerm = (name) => {
    setTrems((prev) => {
      let next = [...prev];
      if (name === 'tremAll') next = prev[0] ? [false, false, false] : [true, true, true];
      else if (name === 'tremService') next[1] = !prev[1];
      else if (name === 'tremPlcy') next[2] = !prev[2];
      next[0] = next[1] && next[2];
      return next;
    });
  };

  const onSubmit = () => {
    if (!trems[0]) { alert('약관에 동의해주세요.'); return; }
    if (userType === 'Company' && !form.companyName.trim()) { alert('상호를 입력하세요.'); return; }
    if (userType === 'Company' && form.bizNo.replace(/\D/g, '').length !== 10) { alert('사업자등록번호 10자리를 입력하세요.'); return; }
    if (!form.name.trim()) { alert('이름을 입력하세요.'); return; }
    if (form.password1 !== form.password) { setErrorMsg('비밀번호가 일치하지 않습니다.'); return; }
    setErrorMsg('');
    if (!EmailRegExp(form.email)) { alert('이메일을 정확히 입력하세요.'); return; }
    if (!PwdRegExp(form.password)) { alert('비밀번호는 영문,숫자,특수기호 포함 8자리 이상 입력하세요.'); return; }
    emailJoin({ ...form, email: form.email.trim(), name: form.name.trim(), companyName: form.companyName.trim(), bizNo: form.bizNo.replace(/\D/g, ''), userType, bizCert: userType === 'Company' ? bizCert : null }, history);
  };

  const handleTrem = (e, type) => {
    e.preventDefault();
    history.push({ pathname: '/policy', state: { type } });
  };

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <Grid container direction="row" justify="center" wrap="nowrap" className={classes.signUpWrap}>
      <Grid item className={classes.card}>
        <Grid container direction="column" justify="center" alignItems="center">
          <GuideMessage
            title="회원가입"
            subTitle={matches ? '환영합니다.<br/> 순식간에 끝나는 초간단 회원가입입니다.' : '환영합니다. 순식간에 끝나는 초간단 회원가입입니다.'}
          />
        </Grid>
        <AuthTabs className={classes.tabs} value={userType} onChange={setUserType} />
        <Box className={clsx(classes.formUserInfoWrap, 'mainFormGroup')}>
          {userType === 'Company' && (
            <React.Fragment>
              <TextField
                name="companyName"
                placeholder="상호"
                variant="outlined"
                fullWidth
                className={clsx('formArea')}
                value={form.companyName}
                autoComplete="organization"
                onChange={handleField}
              />
              <TextField
                name="bizNo"
                placeholder="사업자등록번호 (000-00-00000)"
                variant="outlined"
                fullWidth
                className={clsx('formArea')}
                value={form.bizNo}
                inputProps={{ inputMode: 'numeric', maxLength: 12 }}
                onChange={handleField}
              />
              <BizCertField className="formArea" value={bizCert} onChange={setBizCert} />
            </React.Fragment>
          )}
          <TextField
            name="email"
            placeholder="아이디(e-mail)"
            variant="outlined"
            fullWidth
            className={clsx('formArea')}
            value={form.email}
            autoComplete="username"
            inputProps={{ inputMode: 'email' }}
            onChange={handleField}
          />
          <TextField
            name="password1"
            type="password"
            placeholder="비밀번호(영문,숫자,특수기호 포함 8자리 이상)"
            variant="outlined"
            fullWidth
            className={clsx('formArea')}
            value={form.password1}
            autoComplete="new-password"
            onChange={handleField}
          />
          <TextField
            name="password"
            type="password"
            placeholder="비밀번호 확인"
            variant="outlined"
            fullWidth
            className={clsx('formArea')}
            value={form.password}
            autoComplete="new-password"
            onChange={handleField}
            error={Boolean(errorMsg)}
            helperText={errorMsg}
          />
          <TextField
            name="name"
            placeholder={userType === 'Company' ? '담당자 이름' : '이름'}
            variant="outlined"
            fullWidth
            className={clsx('formArea')}
            value={form.name}
            autoComplete="name"
            onChange={handleField}
          />
        </Box>
        <PhoneVerify purpose="join" onVerified={(uid) => setPhoneVerified(uid)} />
        <Grid container direction="column" justify="flex-start" alignItems="flex-start" className={classes.termsLink}>
          <Grid item>
            <FormControlLabel
              control={<CustomCheckbox name="tremAll" checked={trems[0]} onClick={() => handleTerm('tremAll')} />}
              label={<Typography className={classes.termsText}><b>모두 동의하기</b></Typography>}
            />
          </Grid>
          <Grid item style={{ margin: '10px 0' }}>
            <FormControlLabel
              control={<CustomCheckbox name="tremService" checked={trems[1]} onClick={() => handleTerm('tremService')} />}
              label={(
                <Typography className={classes.termsText}>
                  <span>서비스 이용약관(필수)</span>
                  <a onClick={(e) => handleTrem(e, 'service')} style={{ borderBottom: '1px solid' }}>전체보기</a>
                </Typography>
              )}
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<CustomCheckbox name="tremPlcy" checked={trems[2]} onClick={() => handleTerm('tremPlcy')} />}
              label={(
                <Typography className={classes.termsText}>
                  <span>개인정보취급방침(필수)</span>
                  <a onClick={(e) => handleTrem(e, 'privacy')} style={{ borderBottom: '1px solid' }}>전체보기</a>
                </Typography>
              )}
            />
          </Grid>
        </Grid>
        <CustomButton fullWidth variant="contained" color="primary" onClick={onSubmit}>
          {userType === 'Company' ? '신청하기' : '회원가입완료'}
        </CustomButton>
      </Grid>
    </Grid>
  );
}

export default inject('loginStore')(observer(SignUpPage));
